import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { BdcStockReport, BdcStockReportFigures } from "../../../types/bdc.type";
import { pcs, thaiDate } from "../format";
import KpiRow from "../KpiRow";
import { CARD_SX } from "../styles";
import { pctText } from "./calc";

const SECTION_SX = { ...CARD_SX, p: 1.75, display: "flex", flexDirection: "column", gap: 1 } as const;

const FigureCells = ({ row }: Readonly<{ row: BdcStockReportFigures }>) => (
  <>
    <td className="n">{pcs(row.available)}</td>
    <td className="n">{pcs(row.factory_in)}</td>
    <td className="n">{pcs(row.system_out)}</td>
    <td className="n">
      <b>{pcs(row.over)}</b>
    </td>
    <td className="n">{pctText(row.over_pct)}</td>
    <td className="n">{row.short > 0 ? <span className="short">{pcs(row.short)}</span> : 0}</td>
    <td className="n">{pcs(row.damaged)}</td>
  </>
);

const FIGURE_HEADS = ["ของที่มี", "โรงงาน", "7-11 สั่ง", "เกิน", "% เกิน", "ขาด", "ชำรุด"];

/**
 * รายงาน GM — โรงงาน vs ยอดสั่ง 7-11 vs เกิน over the CLOSED days of a range, per day and per
 * product. Open days are listed (not counted) so the GM can go close them.
 */
export default function ReportView({
  report,
  onOpenDay,
}: Readonly<{ report: BdcStockReport; onOpenDay: (workDate: string) => void }>) {
  const { totals } = report;
  return (
    <Stack gap={1.75}>
      <KpiRow
        items={[
          {
            label: "วันที่ปิดแล้ว",
            value: pcs(report.closed_days),
            sub: report.open_dates.length > 0 ? `ยังไม่ปิด ${report.open_dates.length} วัน (ไม่นับ)` : "ปิดครบทุกวันที่มีข้อมูล",
          },
          { label: "รับจากโรงงาน", value: pcs(totals.factory_in), sub: `ของที่มี ${pcs(totals.available)}` },
          { label: "7-11 สั่ง (ระบบ)", value: pcs(totals.system_out) },
          { label: "เกิน", value: pcs(totals.over), sub: `${pctText(totals.over_pct)} ของของที่มี`, tone: "warn" },
          { label: "ขาด", value: pcs(totals.short), sub: `ชำรุด ${pcs(totals.damaged)}` },
        ]}
      />

      {report.open_dates.length > 0 && (
        <Alert severity="info" data-testid="bdc-stock-open-dates">
          ยังไม่ปิดวัน:{" "}
          {report.open_dates.map((date, index) => (
            <span key={date}>
              {index > 0 && ", "}
              <Box
                component="button"
                type="button"
                onClick={() => onOpenDay(date)}
                sx={{ border: 0, p: 0, background: "none", color: "inherit", textDecoration: "underline", cursor: "pointer", font: "inherit" }}
              >
                {thaiDate(date)}
              </Box>
            </span>
          ))}
        </Alert>
      )}

      <Card sx={SECTION_SX}>
        <Typography fontWeight={600}>รายวัน</Typography>
        <Box sx={{ overflowX: "auto" }}>
          <table className="bdc-t">
            <thead>
              <tr>
                <th>วันสั่ง</th>
                {FIGURE_HEADS.map((head) => (
                  <th key={head} className="n">
                    {head}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {report.days.length === 0 ? (
                <tr>
                  <td colSpan={8} className="c sub">
                    ยังไม่มีวันที่ปิดแล้วในช่วงนี้
                  </td>
                </tr>
              ) : (
                report.days.map((day) => (
                  <tr key={day.work_date} className="click" onClick={() => onOpenDay(day.work_date)}>
                    <td>{thaiDate(day.work_date)}</td>
                    <FigureCells row={day} />
                  </tr>
                ))
              )}
            </tbody>
            {report.days.length > 0 && (
              <tfoot>
                <tr>
                  <td>รวม {report.closed_days} วัน</td>
                  <FigureCells row={totals} />
                </tr>
              </tfoot>
            )}
          </table>
        </Box>
      </Card>

      <Card sx={SECTION_SX}>
        <Typography fontWeight={600}>ต่อสินค้า</Typography>
        <Box sx={{ overflowX: "auto" }}>
          <table className="bdc-t">
            <thead>
              <tr>
                <th>สินค้า</th>
                {FIGURE_HEADS.map((head) => (
                  <th key={head} className="n">
                    {head}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {report.skus.length === 0 ? (
                <tr>
                  <td colSpan={8} className="c sub">
                    ไม่มีข้อมูล
                  </td>
                </tr>
              ) : (
                report.skus.map((sku) => (
                  <tr key={sku.barcode}>
                    <td>
                      {sku.name} <span className="sub">{sku.item_code}</span>
                    </td>
                    <FigureCells row={sku} />
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </Box>
        <Typography className="sub">
          ของที่มี = ยกมา + โรงงาน + รับโอน − โอนออก · % เกิน = เกิน ÷ ของที่มี · นับเฉพาะวันที่ปิดแล้ว
        </Typography>
      </Card>
    </Stack>
  );
}
